import merge from "lodash/merge";
import queryString from "query-string";
import config from "../config/global";
import AuthStorage from "./AuthStorage";
import mockApiService from "./mock-api";
import DEMO_CONFIG from "../config/demo";

// Toggle this to switch between mock and real API
const USE_MOCK_API = DEMO_CONFIG.USE_MOCK_API;

export const fetchApi = async (
  { url, options, payload = {}, dispatch = (f) => f } = required(),
  next = (f) => f,
) => {
  // Use mock API if enabled
  if (USE_MOCK_API) {
    return mockApiHandler(url, options, payload, next);
  }

  try {
    const defaultOptions = {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json;charset=UTF-8",
      },
      credentials: "include",
    };
    const currentOptions = merge(defaultOptions, options);

    // set auth token
    if (AuthStorage.token && !currentOptions.headers.authorization) {
      currentOptions.headers.authorization = `Bearer ${AuthStorage.token}`;
    }

    let fullUrl = config.APP.API_URL + url;
    if (payload && Object.keys(payload).length > 0) {
      if (currentOptions && currentOptions.method === "GET") {
        fullUrl = queryString.stringifyUrl({ url: fullUrl, query: payload });
      } else {
        if (options?.headers["Content-Type"] === "multipart/form-data") {
          delete currentOptions?.headers["Content-Type"];
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (
              key === "assetFiles" ||
              key === "pdfFiles" ||
              key === "images" ||
              key === "newImages" ||
              key === "newPdfFiles" ||
              key === "newMediaFiles" ||
              key === "deleteImages" ||
              key === "deletePdfFiles" ||
              key === "deleteMediaFiles"
            ) {
              value.forEach((file) => {
                formData.append(key, file);
              });
            } else {
              formData.append(key, value);
            }
          });
          currentOptions.body = formData;
        } else {
          currentOptions.body = JSON.stringify(payload);
        }
      }
    }

    if (process.env.NODE_ENV !== "production") {
      console.log("------");
      console.log("Headers - ", currentOptions.headers);
      console.log("------");
      console.log(
        "Call API: url, options, payload",
        fullUrl,
        currentOptions,
        payload,
      );
    }

    // make the fetch call
    const response = await fetch(fullUrl, currentOptions);

    if (process.env.NODE_ENV !== "production") {
      console.log("------");
    }

    if (
      response.ok &&
      (response.status === 204 || response.statusText === "No Content")
    ) {
      next(null, {});
      return {};
    }

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }
    next(null, data || {});

    return data || {};
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.log("Call API Error: ", err);
    }

    if (
      err.statusCode === 403 ||
      err.statusCode === 401 ||
      err.message == "unauthorized user"
    ) {
      console.log("401 Error");
      AuthStorage.sessionDestroy();
    }
    if (err.statusCode === 409) {
      console.log("409 conflict");
    }
    next(err);
    throw err;
  }
};

function required() {
  return Promise.reject({
    status: false,
    message: "FetchApi: missing parameter",
  });
}

// Mock API handler to route requests to mock service
async function mockApiHandler(url, options, payload, next) {
  try {
    let data = null;
    const method = options?.method || "GET";

    // Auth endpoints
    if (url.includes("/api/v1/auth/login")) {
      data = await mockApiService.login(payload);
    } else if (url.includes("/api/v1/auth/register")) {
      data = await mockApiService.register(payload);
    } else if (url.includes("/api/v1/auth/confirm-mail")) {
      data = await mockApiService.confirmMail();
    } else if (url.includes("/api/v1/auth/check-username")) {
      data = await mockApiService.checkUsername(payload.username);
    } else if (url.includes("/api/v1/auth/wall")) {
      data = await mockApiService.setUsername(payload);
    } else if (url.includes("/api/v1/auth/two-factor/send-2fa")) {
      data = await mockApiService.twoFactorSetup();
    } else if (
      url.includes("/api/v1/auth/two-factor") &&
      url.includes("confirm-2fa")
    ) {
      data = await mockApiService.twoFactorConfirmation();
    } else if (
      url.includes("/api/v1/auth/forgot-password") &&
      method === "GET"
    ) {
      data = await mockApiService.resetPassword(payload.email);
    } else if (
      url.includes("/api/v1/auth/forgot-password") &&
      method === "PUT"
    ) {
      data = await mockApiService.confirmResetPassword(payload);
    } else if (url.includes("/api/v1/auth/change-password")) {
      data = await mockApiService.changePassword(payload);
    } else if (url.includes("/api/v1/auth/logout-all-sessions")) {
      data = await mockApiService.logoutAllSessions();
    } else if (url.includes("/api/v1/auth/logout")) {
      data = await mockApiService.logout();
    }

    // Wallet endpoints
    else if (
      url.includes("/api/v1/wallet/plsr/transactions") &&
      method === "POST"
    ) {
      data = await mockApiService.sendPulsar(payload);
    } else if (url.includes("/api/v1/wallet/plsr/transactions")) {
      data = await mockApiService.getTransactions();
    } else if (url.includes("/api/v1/wallet/plsr/address")) {
      data = await mockApiService.generateAddress();
    } else if (url.includes("/api/v1/wallet/plsr/search")) {
      const keyword = url.split("keyword=")[1] || "";
      data = await mockApiService.searchPulsar(keyword);
    } else if (url.includes("/api/v1/wallet/plsr/explorer-records")) {
      data = await mockApiService.explorerRecords();
    } else if (url.includes("/api/v1/wallet/plsr/explorer")) {
      data = await mockApiService.getExplorerData();
    } else if (url.includes("/api/v1/wallet/plsr")) {
      data = await mockApiService.getPulsar();
    }

    // Staking endpoints
    else if (url.includes("/api/v1/staking/stake") && method === "POST") {
      data = await mockApiService.stake(payload);
    } else if (url.includes("/api/v1/staking/withdraw-stake")) {
      data = await mockApiService.withdrawStake(payload);
    } else if (url.includes("/api/v1/staking/user-details")) {
      data = await mockApiService.userStakeDetails();
    }

    // Settings endpoints
    else if (url.includes("/api/v1/profile/hide-username")) {
      const otp = url.split("otp=")[1] || "";
      data = await mockApiService.hideUsername(otp);
    } else if (url.includes("/api/v1/profile/show-username")) {
      const otp = url.split("otp=")[1] || "";
      data = await mockApiService.showUsername(otp);
    } else if (url.includes("/api/v1/profile/transfer-coin-ownership")) {
      data = await mockApiService.transferAllCoins(payload);
    } else if (url.includes("/api/v1/profile/delete-account")) {
      const otp = url.split("otp=")[1] || "";
      data = await mockApiService.deleteAccount(otp);
    }

    // Admin endpoints
    else if (url.includes("/api/v1/admin/notification")) {
      data = await mockApiService.sendNotification(payload);
    } else if (url.includes("/api/v1/notification/")) {
      const id = url.split("/api/v1/notification/")[1];
      data = await mockApiService.getNotification(id);
    }

    // Google OAuth & 2FA endpoints
    else if (url.includes("/api/v1/oauth/google/validate")) {
      data = await mockApiService.login(payload);
    } else if (url.includes("/api/v1/two-factor/google/setup-2fa")) {
      data = await mockApiService.enableGoogleTwoFactor();
    } else if (url.includes("/api/v1/two-factor/google/confirm-2fa")) {
      data = await mockApiService.twoFactorConfirmation();
    } else if (url.includes("/api/v1/two-factor/google/disable-2fa")) {
      const otp = url.split("otp=")[1] || "";
      data = await mockApiService.disableGoogleTwoFactor(otp);
    } else if (url.includes("/api/v1/captcha/google")) {
      data = { status: true, message: "Captcha verified" };
    }

    // Default fallback
    else {
      console.warn(`Mock API: No handler for ${url}`);
      data = { status: true, message: "Mock API response", data: {} };
    }

    if (process.env.NODE_ENV !== "production") {
      console.log("------");
      console.log("Mock API Call:", url, options?.method || "GET");
      console.log("Mock Response:", data);
      console.log("------");
    }

    next(null, data || {});
    return data || {};
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.log("Mock API Error:", err);
    }
    next(err);
    throw err;
  }
}
